/* eslint-disable react/jsx-no-bind */
// @flow
import React, { useState, type Node } from 'react';
import {
    InlineField,
    Flex,
    Box,
    Position,
    InlineFieldActions,
    InlineFieldAction,
    InlineFieldInfoAction,
    type InlineFieldProps,
    Loader,
    Divider
} from '@locus-taxy/neo-ui';
import { Select } from 'common/components/core/select';
import { type SelectOptionType } from 'common/components/core/select/Select';
import { SizeEnum } from 'common/enums/UIEnums';
import DataUtils from 'common/utils/DataUtils';
import theme from './styles.scss';

type SelectValueType =
    | string
    | Array<string>
    | SelectOptionType
    | Array<SelectOptionType>;

export type SelectFieldTypes = {
    ...InlineFieldProps,
    value: SelectValueType,
    options: Array<SelectOptionType>,
    onChange: (option: SelectOptionType | Array<void>) => void,
    isMulti?: boolean,
    isSearchable?: boolean,
    isClearable?: boolean,
    info?: string,
    isLoading?: boolean,
    onInputChange?: (queryString: string) => void,
    closeMenuOnSelect?: boolean,
    onMenuClose?: () => void,
    portal?: boolean
};

const SelectField = ({
    label,
    value,
    options,
    onChange,
    size = 'medium',
    isMulti,
    fullWidth,
    isSearchable,
    isClearable = true,
    closeMenuOnSelect = !isMulti,
    icon,
    info,
    error,
    helperText,
    disabled,
    validated,
    required,
    isLoading,
    onInputChange,
    onMenuClose,
    portal = false
}: SelectFieldTypes): Node => {
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const handleFocus = () => setIsInputFocused(true);
    const handleBlur = () => setIsInputFocused(false);
    const handleValueReset = () =>
        onChange(isMulti ? [] : { value: '', label: '' });

    // hack to close menu properly when opened by select component
    // ES-48820 - https://locussh.atlassian.net/browse/ES-48820?focusedCommentId=250319
    const delayedMenuClose = () => setTimeout(() => setIsMenuOpen(false), 100);

    const handleContainerBlur = () => delayedMenuClose();

    const handleMenuOpen = () => setIsMenuOpen(true);
    const handleMenuClose = () => {
        delayedMenuClose();
        onMenuClose && onMenuClose();
    };
    const handleInputChange = (
        queryString: string,
        { action }: { action: string }
    ) => {
        if (action === 'input-change') {
            onInputChange && onInputChange(queryString);
        }
    };
    const toggleMenu = () => setIsMenuOpen(isOpen => !isOpen);

    const isNotEmptyValue = !DataUtils.isEmpty(value);

    return (
        <InlineField
            label={label}
            size={size}
            isInputFocused={isInputFocused}
            fullWidth={fullWidth}
            icon={icon}
            value={value}
            error={error}
            helperText={helperText}
            disabled={disabled}
            validated={validated}
            required={required}
        >
            <Position position="relative" width="100%">
                <Position
                    position={isMulti ? 'relative' : 'absolute'}
                    width="100%"
                >
                    <Flex
                        alignItems="center"
                        width="100%"
                        onBlur={handleContainerBlur}
                        justifyContent="space-between"
                    >
                        <Box width={isMulti ? '100%' : '85%'} pt={4}>
                            <Select
                                isSearchable={isSearchable}
                                isClearable={false}
                                options={options}
                                onChange={onChange}
                                onInputChange={handleInputChange}
                                selectSize={SizeEnum.Medium}
                                value={value}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                placeholder=""
                                selectType="none"
                                noMarginBottom
                                showPlaceholderIcon={false}
                                isMulti={isMulti}
                                theme={theme}
                                className={theme.statusSelect}
                                onMenuOpen={handleMenuOpen}
                                onMenuClose={handleMenuClose}
                                hideDropdownIndicator
                                disabled={disabled}
                                menuIsOpen={isMenuOpen}
                                openMenuOnFocus
                                closeMenuOnSelect={closeMenuOnSelect}
                                portal={portal}
                                menuPortalZIndex={10000}
                            />
                        </Box>
                        {isLoading && <Loader size="small" color="blue.500" />}
                        <InlineFieldActions>
                            {info ? (
                                <InlineFieldInfoAction info={info} />
                            ) : null}
                            {isNotEmptyValue && isClearable ? (
                                <>
                                    <InlineFieldAction
                                        icon="cross_circle"
                                        onClick={handleValueReset}
                                    />
                                    <Divider
                                        inlineSeparator="vertical"
                                        type="inline"
                                        color="grey.600"
                                    />
                                </>
                            ) : null}
                            <InlineFieldAction
                                onClick={toggleMenu}
                                icon={
                                    isMenuOpen
                                        ? 'arrow_drop_up'
                                        : 'arrow_drop_down'
                                }
                            />
                        </InlineFieldActions>
                    </Flex>
                </Position>
            </Position>
        </InlineField>
    );
};

export default SelectField;
